import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getNeonAuthClient } from '../lib/neon-auth';
import { Button, Input } from '@lms/ui';
import { CheckCircle2, ArrowRight, XCircle } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();

  const resetPasswordSchema = useMemo(() => {
    return z
      .object({
        password: z.string().min(8, t(copy.resetPassword.validation.passwordMin)),
        confirmPassword: z.string().min(8, t(copy.resetPassword.validation.confirmPasswordMin)),
      })
      .refine(data => data.password === data.confirmPassword, {
        message: t(copy.resetPassword.validation.passwordsDontMatch),
        path: ['confirmPassword'],
      });
  }, [t]);

  type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit = async (data: ResetPasswordInput) => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t(copy.resetPassword.tokenNotFound));
      return;
    }

    try {
      const response = await getNeonAuthClient().resetPassword({
        token,
        newPassword: data.password,
      });
      if (response.error) throw new Error(response.error.message);
      setStatus('success');
    } catch (e: unknown) {
      const error = e as Error;
      setStatus('error');
      setErrorMessage(error.message || t(copy.resetPassword.generalError));
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-surface p-4">
        <div className="w-full max-w-md bg-surface-container-lowest rounded-3xl p-8 sm:p-10 shadow-level3 flex flex-col items-center text-center">
          <XCircle className="w-16 h-16 text-error mb-6" />
          <h1 className="font-headline font-bold text-2xl text-on-surface mb-3">
            {t(copy.resetPassword.invalidTokenTitle)}
          </h1>
          <p className="text-on-surface-variant/80 text-sm mb-8">
            {t(copy.resetPassword.invalidTokenDesc)}
          </p>
          <Button
            onClick={() => navigate('/login')}
            variant="primary"
            className="w-full rounded-xl"
          >
            {t(copy.resetPassword.backToLogin)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-surface font-body text-on-surface p-4 relative overflow-hidden">
      {/* Background Ambient Layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface to-surface-container-lowest -z-10" />
      <div className="absolute top-1/4 -right-48 w-[500px] h-[500px] bg-primary opacity-[0.03] rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-secondary opacity-[0.02] rounded-full blur-[80px] pointer-events-none" />

      {/* Language Switcher */}
      <div className="absolute top-8 right-8 z-20">
        <div className="flex items-center bg-surface-container rounded-lg p-1 shadow-level1">
          <button
            type="button"
            onClick={() => setLanguage('id')}
            className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
              language === 'id'
                ? 'bg-primary text-white shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            ID
          </button>
          <button
            type="button"
            onClick={() => setLanguage('ar')}
            className={`px-3 py-1 text-[11px] font-bold rounded transition-all ${
              language === 'ar'
                ? 'bg-primary text-white shadow-sm'
                : 'text-on-surface-variant hover:bg-surface-container-high'
            }`}
          >
            AR
          </button>
        </div>
      </div>

      {/* Main Card */}
      <div className="w-full max-w-md bg-surface-container-lowest rounded-3xl p-8 sm:p-10 shadow-level3 relative z-10 flex flex-col items-center animate-in fade-in zoom-in-95 duration-700">
        <img
          alt="Tarbiyah Heritage Logo"
          className="h-14 w-auto mb-10 opacity-90"
          src="/logo.png"
        />

        {status === 'success' ? (
          <div className="flex flex-col items-center text-center animate-in slide-in-from-bottom-4 fade-in duration-500">
            <div className="w-20 h-20 rounded-full bg-success-container/30 flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-success" />
            </div>
            <h1 className="font-headline font-bold text-2xl text-on-surface mb-3">
              {t(copy.resetPassword.successTitle)}
            </h1>
            <p className="text-on-surface-variant/80 text-sm mb-8">
              {t(copy.resetPassword.successDesc)}
            </p>
            <Button
              onClick={() => navigate('/login')}
              variant="primary"
              size="lg"
              className="w-full rounded-xl flex items-center justify-center gap-3 group"
            >
              {t(copy.resetPassword.backToLogin)}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        ) : (
          <div className="w-full">
            <div className="text-center mb-8">
              <h1 className="font-headline font-bold text-2xl text-on-surface mb-2">
                {t(copy.resetPassword.title)}
              </h1>
              <p className="text-on-surface-variant/80 text-sm">{t(copy.resetPassword.subtitle)}</p>
            </div>

            {status === 'error' && (
              <div className="mb-6 p-4 rounded-xl text-sm font-medium bg-error-container/30 text-on-error-container shadow-level1 flex items-start gap-3">
                <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <Input
                label={t(copy.resetPassword.newPasswordLabel)}
                type="password"
                placeholder={t(copy.resetPassword.newPasswordPlaceholder)}
                {...register('password')}
                error={errors.password?.message}
              />

              <Input
                label={t(copy.resetPassword.confirmPasswordLabel)}
                type="password"
                placeholder={t(copy.resetPassword.confirmPasswordPlaceholder)}
                {...register('confirmPassword')}
                error={errors.confirmPassword?.message}
              />

              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  isLoading={isSubmitting}
                  className="w-full rounded-xl"
                >
                  {t(copy.resetPassword.submitBtn)}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
